import { useMemo, useState } from "react";
import { z } from "zod";
import {
    ProbationFormSchema,
    type ProbationForm,
    type ProbationQuestion,
    type SectionType,
} from "../schemas/index.ts";
import { useForm } from "@tanstack/react-form";
import { ApiError, postApi } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { API_PROBATION_SAVE, API_PROBATION_SUBMIT } from "../../../../../config/Endpoints.js";
import { accessToken } from "../../../../utils/auth.ts";
import {
    isProbationSectionDraft,
    isProbationSectionSubmitted,
} from "../utils/probation-submission-status.ts";
import {
    findHrRecommendationQuestionIndex,
    isHrExtendRecommendation,
} from "../utils/hr-submit-button.ts";
import { timePeriodToDays } from "../../../ui/time-period-select/time-period-utils.ts";
import {
    isInductionYes,
    isSqeInductionDurationQuestion,
    SQE_INDUCTION_QUESTION_ID,
} from "../utils/sqe-induction-questions.ts";
import { sqeManagerRatingPercentage } from "../utils/sqe-manager-rating.ts";

export type ProbationFormState = "initial" | "draft" | "submitted";

interface UseProbationFormProps {
    questions: ProbationQuestion[];
    cycleId: number;
    sectionType: SectionType;
    onSuccess: () => void;
    conditionalInductionDuration?: boolean;
}

function buildDefaultValues(
    questions: ProbationQuestion[],
    sectionType: SectionType,
): ProbationForm {
    return {
        answers: questions.map((q) => {
            let initialValue: string | string[] = q.answer ?? "";

            if (q.questionType === "CHECKBOX") {
                try {
                    initialValue = typeof q.answer === "string" ? JSON.parse(q.answer) : [];
                } catch {
                    initialValue = [];
                }
            }

            return {
                reviewId: q.reviewId,
                answer: initialValue,
                ...(sectionType === "SQE_MANAGER" ? { textAnswers: q.textAnswers ?? "" } : {}),
            };
        }) as ProbationForm["answers"],
        ...(sectionType === "HR"
            ? { extensionPeriod: { amount: 1, unit: "month" as const } }
            : {}),
    };
}

export function useProbationForm({ questions, cycleId, sectionType, onSuccess, conditionalInductionDuration = false }: UseProbationFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const recommendationQuestionIndex =
        sectionType === "HR" ? findHrRecommendationQuestionIndex(questions) : -1;
    const inductionQuestionIndex = conditionalInductionDuration
        ? questions.findIndex((question) => question.questionId === SQE_INDUCTION_QUESTION_ID)
        : -1;
    const durationQuestionIndex = conditionalInductionDuration
        ? questions.findIndex(isSqeInductionDurationQuestion)
        : -1;
    const formSchema = useMemo(() => {
        if (inductionQuestionIndex < 0 || durationQuestionIndex < 0) return ProbationFormSchema;
        return z.preprocess((rawValue) => {
            if (rawValue == null || typeof rawValue !== "object" || !("answers" in rawValue)) return rawValue;
            const formValue = rawValue as ProbationForm;
            if (!Array.isArray(formValue.answers) || isInductionYes(formValue.answers[inductionQuestionIndex]?.answer)) {
                return rawValue;
            }
            return {
                ...formValue,
                answers: formValue.answers.map((answer, index) =>
                    index === durationQuestionIndex ? { ...answer, answer: "Not applicable" } : answer,
                ),
            };
        }, ProbationFormSchema);
    }, [inductionQuestionIndex, durationQuestionIndex]);

    const answersForApi = (answers: ProbationForm["answers"]) =>
        inductionQuestionIndex >= 0 && durationQuestionIndex >= 0
            && !isInductionYes(answers[inductionQuestionIndex]?.answer)
            ? answers.filter((_, index) => index !== durationQuestionIndex)
            : answers;

    const form = useForm({
        defaultValues: buildDefaultValues(questions, sectionType),
        validators: {
            onSubmit: formSchema,
        },
        onSubmit: async ({ value }) => {
            const recommendationAnswer =
                recommendationQuestionIndex >= 0
                    ? (value.answers[recommendationQuestionIndex]?.answer as string | undefined)
                    : undefined;

            if (
                sectionType === "HR" &&
                isHrExtendRecommendation(recommendationAnswer) &&
                (value.extensionPeriod == null || value.extensionPeriod.amount < 1)
            ) {
                showError("Probation extension period is required.");
                return;
            }

            const transformedAnswers = answersForApi(value.answers).map((item) => ({
                ...item,
                answer: Array.isArray(item.answer)
                    ? JSON.stringify(item.answer)
                    : item.answer,
            }));

            await handleAPISubmit(transformedAnswers, value, recommendationAnswer);
        },
        onSubmitInvalid: () => {
            showError("Please complete all required fields before submitting.");
        },
    });

    const isSubmitted = isProbationSectionSubmitted(questions);
    const isDraft = isProbationSectionDraft(questions);
    const state: ProbationFormState = isSubmitted ? "submitted" : isDraft ? "draft" : "initial";

    const formatAnswerForAPI = (answer: string | string[]) => {
        return Array.isArray(answer) ? JSON.stringify(answer) : answer;
    };

    const buildPayload = (
        answers: ProbationForm["answers"],
        value: ProbationForm,
        recommendationAnswer?: string,
    ) => {
        const payload: {
            cycleId: number;
            sectionType: SectionType;
            answers: ProbationForm["answers"];
            extensionDays?: number;
            rating?: number;
        } = {
            cycleId,
            sectionType,
            answers,
        };

        if (sectionType === "SQE_MANAGER") {
            payload.rating = sqeManagerRatingPercentage(questions, value.answers);
        }

        if (
            sectionType === "HR" &&
            isHrExtendRecommendation(recommendationAnswer) &&
            value.extensionPeriod != null
        ) {
            payload.extensionDays = timePeriodToDays(
                value.extensionPeriod.amount,
                value.extensionPeriod.unit,
            );
        }

        return payload;
    };

    const handleSaveDraft = async () => {
        setIsSubmitting(true);
        const value = form.state.values;
        const recommendationAnswer =
            recommendationQuestionIndex >= 0
                ? (value.answers[recommendationQuestionIndex]?.answer as string | undefined)
                : undefined;

        if (
            sectionType === "HR" &&
            isHrExtendRecommendation(recommendationAnswer) &&
            (value.extensionPeriod == null || value.extensionPeriod.amount < 1)
        ) {
            showError("Probation extension period is required.");
            setIsSubmitting(false);
            return;
        }

        const formValues = answersForApi(value.answers).map((item) => ({
            ...item,
            answer: formatAnswerForAPI(item.answer),
        }));

        const payload = buildPayload(formValues, value, recommendationAnswer);

        try {
            const { data } = await postApi<{ message?: string }>(
                API_PROBATION_SAVE,
                accessToken(),
                payload as Record<string, unknown>,
            );
            const msg =
                data != null &&
                typeof data === "object" &&
                "message" in data &&
                typeof data.message === "string"
                    ? data.message
                    : "Draft saved.";
            showSuccess(msg);
        } catch (error) {
            const msg = error instanceof ApiError ? error.message : "Something went wrong. Please try again.";
            showError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        await form.handleSubmit();
    };

    const handleAPISubmit = async (
        answers: ProbationForm["answers"],
        value: ProbationForm,
        recommendationAnswer?: string,
    ) => {
        setIsSubmitting(true);

        try {
            const payload = buildPayload(answers, value, recommendationAnswer);
            await postApi(
                API_PROBATION_SUBMIT,
                accessToken(),
                payload as Record<string, unknown>,
            );
            showSuccess("Probation assessment submitted.");
            onSuccess();
        } catch (error) {
            const msg = error instanceof ApiError ? error.message : "Something went wrong. Please try again.";
            showError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        form,
        isSubmitting,
        state,
        recommendationQuestionIndex,
        handleSaveDraft,
        handleSubmit,
    };
}
